// Server functions for the investor role.
// Each investor is one row in `programs` with owner_user_id = user.id.
// A newly registered investor starts active=false; admin toggles active=true
// via convertProgram to include them in outbound paid submission targeting.

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { slugifyHandle, validateHandle } from "@/lib/investor-handle";
import { isDisposableEmail } from "@/lib/email-policy";
import { hasFounderFootprint } from "@/lib/account-guards.server";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
  return supabaseAdmin;
}

function randomRefCode(): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 8; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

async function ensureRefCode(): Promise<string> {
  const supabaseAdmin = await getSupabaseAdmin();
  for (let i = 0; i < 6; i++) {
    const code = randomRefCode();
    const { data } = await supabaseAdmin
      .from("programs").select("id").eq("ref_code", code).maybeSingle();
    if (!data) return code;
  }
  return randomRefCode() + Date.now().toString(36).slice(-4);
}

async function isHandleFree(handle: string, exceptProgramId?: string): Promise<boolean> {
  const supabaseAdmin = await getSupabaseAdmin();
  const q = supabaseAdmin.from("programs").select("id").ilike("handle", handle);
  const { data } = await q;
  if (!data || data.length === 0) return true;
  if (exceptProgramId && data.every((r) => r.id === exceptProgramId)) return true;
  return false;
}

async function suggestUniqueHandle(name: string): Promise<string> {
  const base = slugifyHandle(name);
  const v = validateHandle(base);
  const seed = v.ok ? v.handle : "investor";
  if (await isHandleFree(seed)) return seed;
  for (let i = 2; i < 200; i++) {
    const candidate = `${seed}-${i}`.slice(0, 30);
    if (await isHandleFree(candidate)) return candidate;
  }
  return `${seed.slice(0, 22)}-${Date.now().toString(36).slice(-4)}`;
}

export type InvestorInput = {
  name: string;
  type?: string | null;
  thesis?: string | null;
  stage_focus?: string[] | null;
  sector_focus?: string[] | null;
  region?: string | null;
  contact_email?: string | null;
  handle?: string | null;
  logo_url?: string | null;
  invite_blurb?: string | null;
  open_to_program?: boolean | null;
};

const INVESTOR_SELECT =
  "id, name, type, thesis, stage_focus, sector_focus, region, contact_email, active, ref_code, handle, logo_url, invite_blurb, open_to_program, created_at";

export const registerInvestor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: InvestorInput) => {
    if (!input?.name || typeof input.name !== "string") throw new Error("name required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const userId = context.userId;
    const email = (context.claims as { email?: string } | undefined)?.email ?? null;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error("Verified email required. Please sign in via magic link first.");
    }
    if (isDisposableEmail(email)) {
      throw new Error("Please use a permanent work email - temporary/disposable addresses aren't allowed for investor accounts.");
    }

    // Idempotent: if already registered as investor, return existing row.
    const { data: existing } = await supabaseAdmin
      .from("programs").select("id, ref_code, handle")
      .eq("owner_user_id", userId).maybeSingle();
    if (existing) {
      await supabaseAdmin
        .from("profiles").update({ role: "investor", email })
        .eq("id", userId).in("role", ["founder", "investor"]);
      return { program: existing };
    }

    // Enforce one-role-per-email: reject staff/admin accounts entirely.
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("role").eq("id", userId).maybeSingle();
    const currentRole = (profile as { role?: string } | null)?.role ?? null;
    if (currentRole === "admin" || currentRole === "super_admin") {
      throw new Error(
        "This is a staff/admin account and can't be used as an investor. Please register with a different email.",
      );
    }
    if (await hasFounderFootprint(userId)) {
      throw new Error(
        "This email is already registered as a founder. Please use a different email for your investor account.",
      );
    }

    // Never touch admin/super_admin.
    if (currentRole !== "admin" && currentRole !== "super_admin") {
      await supabaseAdmin
        .from("profiles")
        .upsert({ id: userId, email, role: "investor" }, { onConflict: "id" });
    }

    const refCode = await ensureRefCode();
    const handle = await suggestUniqueHandle(data.name);
    const { data: inserted, error } = await supabaseAdmin
      .from("programs")
      .insert({
        name: data.name,
        type: data.type ?? "vc",
        thesis: data.thesis ?? null,
        stage_focus: data.stage_focus ?? null,
        sector_focus: data.sector_focus ?? null,
        region: data.region ?? null,
        contact_email: email, // force verified auth email; ignore client value
        owner_user_id: userId,
        ref_code: refCode,
        handle,
        active: false,
        open_to_program: data.open_to_program ?? false,
      })
      .select("id, ref_code, handle").single();
    if (error) throw error;
    return { program: inserted };
  });

export const getMyInvestor = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("programs").select(INVESTOR_SELECT)
      .eq("owner_user_id", context.userId).maybeSingle();
    if (error) throw error;
    return { investor: data };
  });

export const updateMyInvestor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: InvestorInput) => {
    if (!input?.name || typeof input.name !== "string") throw new Error("name required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: mine } = await supabaseAdmin
      .from("programs").select("id").eq("owner_user_id", context.userId).maybeSingle();

    let handleToWrite: string | null | undefined = undefined;
    if (typeof data.handle === "string" && data.handle.length > 0) {
      const v = validateHandle(data.handle);
      if (!v.ok) throw new Error(`Invalid handle: ${v.reason}`);
      const free = await isHandleFree(v.handle, mine?.id);
      if (!free) throw new Error("Handle already taken");
      handleToWrite = v.handle;
    }

    // Partial update: only overwrite fields explicitly present in input.
    const payload: Record<string, unknown> = {
      name: data.name,
      updated_at: new Date().toISOString(),
    };
    if ("type" in data) payload.type = data.type ?? null;
    if ("thesis" in data) payload.thesis = data.thesis ?? null;
    if ("stage_focus" in data) payload.stage_focus = data.stage_focus ?? null;
    if ("sector_focus" in data) payload.sector_focus = data.sector_focus ?? null;
    if ("region" in data) payload.region = data.region ?? null;
    // Always force contact_email to the caller's verified auth email - ignore client-supplied value.
    {
      const verified = (context.claims as { email?: string } | undefined)?.email ?? null;
      if (verified && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(verified)) {
        payload.contact_email = verified;
      }
    }
    if ("logo_url" in data) payload.logo_url = data.logo_url ?? null;
    if ("invite_blurb" in data) payload.invite_blurb = data.invite_blurb ?? null;
    if ("open_to_program" in data) payload.open_to_program = !!data.open_to_program;
    if (handleToWrite !== undefined) payload.handle = handleToWrite;

    const { data: row, error } = await supabaseAdmin
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from("programs").update(payload as any)
      .eq("owner_user_id", context.userId).select(INVESTOR_SELECT).maybeSingle();
    if (error) throw error;
    return { investor: row };
  });

// Live availability check for the investor dashboard picker.
export const checkHandleAvailability = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { handle: string }) => input)
  .handler(async ({ data, context }) => {
    const v = validateHandle(data.handle);
    if (!v.ok) return { available: false, reason: v.reason };
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: mine } = await supabaseAdmin
      .from("programs").select("id").eq("owner_user_id", context.userId).maybeSingle();
    const free = await isHandleFree(v.handle, mine?.id);
    return { available: free, reason: free ? "ok" : "taken", handle: v.handle };
  });

// Upload a logo file (base64) into the private investor-assets bucket.
// Returns the storage path stored in programs.logo_url.
export const uploadInvestorLogo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { fileName: string; contentBase64: string; contentType: string }) => {
    if (!input?.contentBase64) throw new Error("content required");
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(input.contentType || "")) {
      throw new Error("Unsupported image type");
    }
    return input;
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: mine } = await supabaseAdmin
      .from("programs").select("id").eq("owner_user_id", context.userId).maybeSingle();
    if (!mine) throw new Error("Register as an investor first");
    const bytes = Uint8Array.from(atob(data.contentBase64), (c) => c.charCodeAt(0));
    if (bytes.byteLength > 1_500_000) throw new Error("Logo must be under 1.5 MB");
    const ext = data.contentType === "image/svg+xml" ? "svg"
      : data.contentType === "image/png" ? "png"
      : data.contentType === "image/webp" ? "webp"
      : "jpg";
    const path = `${mine.id}/logo-${Date.now()}.${ext}`;
    const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    const { error: upErr } = await supabaseAdmin.storage
      .from("investor-assets")
      .upload(path, arrayBuffer, { contentType: data.contentType, upsert: true });
    if (upErr) throw upErr;
    await supabaseAdmin
      .from("programs").update({ logo_url: path, updated_at: new Date().toISOString() })
      .eq("id", mine.id);
    return { path };
  });

// Public resolver for the branded landing page. No auth. Only exposes
// safe branding fields - never owner_user_id / notes / crm.
export type PublicInvestorLanding = {
  id: string;
  name: string;
  handle: string;
  ref_code: string | null;
  invite_blurb: string | null;
  has_logo: boolean;
};

export const resolveHandleForLanding = createServerFn({ method: "GET" })
  .inputValidator((input: { handle: string }) => input)
  .handler(async ({ data }): Promise<{ investor: PublicInvestorLanding | null }> => {
    const v = validateHandle(data.handle);
    if (!v.ok) return { investor: null };
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: row } = await supabaseAdmin
      .from("programs")
      .select("id, name, handle, ref_code, invite_blurb, logo_url, owner_user_id")
      .ilike("handle", v.handle)
      .maybeSingle();
    if (!row || !row.owner_user_id) return { investor: null };
    return {
      investor: {
        id: row.id,
        name: row.name,
        handle: (row.handle ?? v.handle).toLowerCase(),
        ref_code: row.ref_code,
        invite_blurb: row.invite_blurb,
        has_logo: !!row.logo_url,
      },
    };
  });

// Signed short-lived URL for the investor's own logo preview.
export const getMyLogoUrl = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: mine } = await supabaseAdmin
      .from("programs").select("logo_url")
      .eq("owner_user_id", context.userId).maybeSingle();
    if (!mine?.logo_url) return { url: null as string | null };
    const { data: signed } = await supabaseAdmin.storage
      .from("investor-assets").createSignedUrl(mine.logo_url, 600);
    return { url: signed?.signedUrl ?? null };
  });

export type InvestorReferral = {
  id: string;
  deck_id: string;
  delivered_at: string;
  emailed_at: string | null;
  company: string;
  one_pager_pdf_path: string | null;
  overall_score: number | null;
};

export const getMyReferrals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: prog } = await supabaseAdmin
      .from("programs").select("id")
      .eq("owner_user_id", context.userId).maybeSingle();
    if (!prog) return { referrals: [] as InvestorReferral[] };

    const { data, error } = await supabaseAdmin
      .from("referral_deliveries")
      .select("id, deck_id, delivered_at, emailed_at, one_pager_content, one_pager_pdf_path")
      .eq("program_id", prog.id)
      .order("delivered_at", { ascending: false });
    if (error) throw error;

    const deckIds = (data ?? []).map((r) => r.deck_id).filter(Boolean);
    const scoresByDeck = new Map<string, number | null>();
    if (deckIds.length) {
      const { data: scoreRows } = await supabaseAdmin
        .from("scores")
        .select("deck_id, overall_score, created_at")
        .in("deck_id", deckIds)
        .order("created_at", { ascending: false });
      for (const s of scoreRows ?? []) {
        if (!scoresByDeck.has(s.deck_id as string)) {
          scoresByDeck.set(s.deck_id as string, (s.overall_score as number | null) ?? null);
        }
      }
    }

    const referrals: InvestorReferral[] = (data ?? []).map((r) => {
      const c = r.one_pager_content as { headline?: string } | null;
      return {
        id: r.id,
        deck_id: r.deck_id,
        delivered_at: r.delivered_at,
        emailed_at: r.emailed_at,
        company: (c?.headline && c.headline.trim()) || "Untitled",
        one_pager_pdf_path: r.one_pager_pdf_path ?? null,
        overall_score: scoresByDeck.get(r.deck_id) ?? null,
      };
    });
    return { referrals };
  });

export const getReferralPdfUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { referralId: string }) => {
    if (!input?.referralId) throw new Error("referralId required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: row } = await supabaseAdmin
      .from("referral_deliveries")
      .select("id, one_pager_pdf_path, programs!inner(owner_user_id)")
      .eq("id", data.referralId).maybeSingle();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const owner = (row as any)?.programs?.owner_user_id as string | undefined;
    if (!row || owner !== context.userId) throw new Error("Forbidden");
    if (!row.one_pager_pdf_path) return { url: null as string | null };
    const { data: signed } = await supabaseAdmin.storage
      .from("decks").createSignedUrl(row.one_pager_pdf_path, 600);
    return { url: signed?.signedUrl ?? null };
  });

// Full referral detail for a specific deck. Ownership check: the caller must
// own a referral_deliveries row (via program.owner_user_id = auth.uid()) for
// this deck. Returns everything the investor needs to review the deck the
// same way admins do: signed deck PDF URL, full score/analysis, one-pager URL.
export type ReferralDetail = {
  deck: { id: string; file_name: string | null; pdf_url: string | null };
  score: {
    overall_score: number | null;
    verdict: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    slide_flags: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fixes: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analysis: any;
  } | null;
  one_pager: { url: string | null; headline: string | null };
  company: string;
  investor: { id: string; name: string };
};

export const getReferralDetail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { deckId: string }) => {
    if (!input?.deckId) throw new Error("deckId required");
    return input;
  })
  .handler(async ({ data, context }): Promise<ReferralDetail> => {
    const supabaseAdmin = await getSupabaseAdmin();

    // Ownership: find a referral delivery for this deck whose program is owned
    // by the caller. This is the ONLY gate - never expose otherwise.
    const { data: delivery } = await supabaseAdmin
      .from("referral_deliveries")
      .select("id, deck_id, one_pager_pdf_path, one_pager_content, program_id, programs!inner(id, name, owner_user_id)")
      .eq("deck_id", data.deckId)
      .maybeSingle();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const program = (delivery as any)?.programs as
      | { id: string; name: string; owner_user_id: string | null }
      | undefined;
    if (!delivery || !program || program.owner_user_id !== context.userId) {
      throw new Error("Forbidden");
    }

    const [{ data: deck }, { data: score }] = await Promise.all([
      supabaseAdmin
        .from("decks")
        .select("id, file_name, file_path")
        .eq("id", data.deckId).maybeSingle(),
      supabaseAdmin
        .from("scores")
        .select("overall_score, verdict, slide_flags, fixes, analysis, created_at")
        .eq("deck_id", data.deckId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    let deckUrl: string | null = null;
    if (deck?.file_path) {
      const { data: signed } = await supabaseAdmin.storage
        .from("decks").createSignedUrl(deck.file_path, 600);
      deckUrl = signed?.signedUrl ?? null;
    }

    let onePagerUrl: string | null = null;
    if (delivery.one_pager_pdf_path) {
      const { data: signed } = await supabaseAdmin.storage
        .from("decks").createSignedUrl(delivery.one_pager_pdf_path, 600);
      onePagerUrl = signed?.signedUrl ?? null;
    }

    const opc = delivery.one_pager_content as { headline?: string } | null;
    const headline = (opc?.headline && opc.headline.trim()) || null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const companyFromAnalysis = ((score?.analysis as any)?.deck_meta?.company_name as string | undefined) ?? null;
    const company =
      companyFromAnalysis ||
      headline ||
      deck?.file_name?.replace(/\.pdf$/i, "") ||
      "Untitled";

    return {
      deck: { id: data.deckId, file_name: deck?.file_name ?? null, pdf_url: deckUrl },
      score: score
        ? {
            overall_score: score.overall_score as number | null,
            verdict: score.verdict as string | null,
            slide_flags: score.slide_flags,
            fixes: score.fixes,
            analysis: score.analysis,
          }
        : null,
      one_pager: { url: onePagerUrl, headline },
      company,
      investor: { id: program.id, name: program.name },
    };
  });
